import moment from 'moment';
import {
  findKey,
  get,
  isEmpty,
  isEqual,
  isNull,
  isString,
  pickBy,
  some,
  values,
  mapValues,
} from 'lodash';

import { TEMPORAL_BINNING_FORMAT_TO_TOKENS_MAP } from 'constants/binning';

const INVALID_DATE_STRING = 'Invalid Date';
/**
 * converts a date token object to the correctly formatted string. Works for
 * periodic and non-periodic binning. We use moment.js to format the date.
 * If tokenObj is already a string, this function just returns it, assuming
 * the value has already been converted.
 *
 * @param  {Object|String}  tokenObj        object with date tokens and their values
 * @param  {Object}         customisations  customisations object for the binned channel
 * @return {String|Null}                    formatted date string or null if no change required
 *
 * Non-periodic Examples
 * @example {year: 1999} --> '1999'
 * @example {year: 1999, month: 3} --> 'Apr 1999'
 * @example {year: 1999, month: 0, date: 1} --> 'Apr 1, 1999'
 *
 * Periodic Examples
 * @example {day: 0} --> 'Sunday'
 * @example {minute: 33} --> '33'
 * @example {month: 2} --> 'Mar'
 *
 * Examples that require no format change
 * @example 'Apr 1999' --> null
 * @example {} --> null
 */
function convertToDateString(tokenObj, customisations) {
  if (isString(tokenObj) || isEmpty(tokenObj)) {
    return null;
  }

  // the agg pipeline builder returns null when any of the date operators
  // are applied to a non-date
  if (some(values(tokenObj), isNull)) {
    return INVALID_DATE_STRING;
  }
  /**
   * construct a date object with the date/time tokens set to their values, by
   * using moment().set() with object parsing.
   *
   * @see https://momentjs.com/docs/#/get-set/set/
   *
   * We need to start from a reference month that has 31 days, otherwise
   * moment uses the current date and setting {date: 31} could roll over
   * into the next month.
   */
  const date = moment('2018-01-01').set(tokenObj);

  // get format string from customisations if present
  // otherwise get the correct format string according to above mapping
  const formatStr =
    get(customisations, 'dateTimeFormatting.value') ||
    findKey(TEMPORAL_BINNING_FORMAT_TO_TOKENS_MAP, val =>
      isEqual(val, Object.keys(tokenObj))
    );

  // if date is null, this returns 'Invalid Date'
  return date.format(formatStr);
}

/**
 * converts the date object into a number purely for use with sorting and ordering.
 * If the date object is invalid, we represent it as NEGATIVE_INFINITY so it's always the first element when ordering.
 * Otherwise, we turn it into a close-enough number for sorting, based around milliseconds since the epoch.
 *
 * As long as all the tokenObj's have been constructed using the same set of tokens, they will sort amongst themselves correctly
 * @param {Object} tokenObj        object with date tokens and their values
 * @return {Number}                representation as milliseconds on the epoch, or NEGATIVE_INFINITY for invalid input
 */
function convertToDateNumber(tokenObj) {
  // the agg pipeline builder returns null when any of the date operators
  // are applied to a non-date
  if (some(values(tokenObj), isNull)) {
    return Number.NEGATIVE_INFINITY;
  }

  return moment('2018-01-01')
    .set(tokenObj)
    .valueOf();
}

/**
 * converts date token objects into formatted date strings for each channel
 * that has temporal binning enabled.
 *
 * @param  {Object} stageData   object containing documents returned from the agg pipeline & channels object in the metadata
 * @return {Object}            object containing documents with date format strings as
 *                             values for all temporally binned channels.
 */
function binDocumentsTemporal(stageData) {
  const {
    environment: { channels, customisations },
  } = stageData;

  // creates an array of all channel names with temporal binning enabled
  const binnedChannels = Object.keys(
    pickBy(channels, encoding => {
      return encoding.temporalBinning;
    })
  );

  // return early if there are no changes to the documents required
  if (isEmpty(binnedChannels)) {
    return {};
  }

  const documents = stageData.documents.map(document => {
    return mapValues(document, (value, channel) => {
      if (binnedChannels.includes(channel)) {
        const converted = convertToDateString(
          value,
          customisations.channels[channel]
        );
        return converted === null
          ? value
          : {
              raw: convertToDateNumber(value), // we turn the date object into milliseconds, so we can do ordering
              display: converted,
            };
      }
      return value;
    });
  });

  return { documents };
}

export default {
  input: [
    'environment.channels.*.temporalBinning',
    'environment.customisations.channels.*.dateTimeFormatting',
    'documents',
  ],
  fn: binDocumentsTemporal,
};
export { binDocumentsTemporal, convertToDateString, convertToDateNumber };
